"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OtherModesScene = void 0;
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const share2_svg_1 = __importDefault(require("../../assets/svg/share2.svg"));
const Global_1 = require("../Global");
const ui_1 = require("@pixi/ui");
const MenuButton_1 = require("../blocks/MenuButton");
const i18next_1 = __importDefault(require("i18next"));
const interfaces_1 = require("../../../shared/interfaces");
const BattleSprite_1 = require("../objects/BattleSprite");
const settings_1 = require("../config/settings");
const MenuScene_1 = require("./MenuScene");
const GuessSprite_1 = require("../objects/GuessSprite");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class OtherModesScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager, settings_1.Settings.othersColor);
        this.top = 60;
        this.musicVolume = settings_1.Settings.loudMusicVolume;
        // We are on mange page, leave rooms
        this.gameManager.leaveRoom();
        // Share button
        if (navigator === null || navigator === void 0 ? void 0 : navigator.share) {
            const tshare = pixi_js_1.Sprite.from(share2_svg_1.default);
            tshare.anchor.set(0.5);
            const btn = new ui_1.ButtonContainer(tshare);
            this.sigs.push(btn.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield navigator.share({
                        title: settings_1.Settings.title,
                        text: i18next_1.default.t('battle.share', { title: settings_1.Settings.title }),
                        url: process.env.SHARE_URL
                    });
                }
                catch (err) {
                    console.warn(err);
                }
            })));
            this.tshare = this.addChild(btn);
        }
        this.scrollBox = new ui_1.ScrollBox({
            elementsMargin: (0, Global_1.px)(24),
            width: this.scrollSize.width,
            height: this.scrollSize.height,
            bottomPadding: (0, Global_1.px)(20),
            topPadding: (0, Global_1.px)(10),
            disableEasing: false,
            type: 'vertical',
            globalScroll: true,
            shiftScroll: false,
            disableDynamicRendering: false,
            items: this.makeMenu()
        });
        this.addChild(this.scrollBox);
        this.subs.add(gameManager.room$.subscribe((room) => __awaiter(this, void 0, void 0, function* () {
            const field = (this.textfield);
            if (field && !this.isDeinited) {
                if ((room === null || room === void 0 ? void 0 : room.type) === interfaces_1.GameType.GuessFriends) {
                    yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateGuess, field);
                }
                else if ((room === null || room === void 0 ? void 0 : room.type) === interfaces_1.GameType.BattleFriends) { // I mean, hopefully we don't have another type here
                    yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateBattle, field);
                }
                else {
                    // No match, turn to red
                    field.children.filter((c) => (c instanceof pixi_js_1.Text)).forEach((t) => {
                        var _a;
                        const text = t;
                        if (field.value && ((_a = text.text) === null || _a === void 0 ? void 0 : _a.length) === 6) {
                            text.style.fill = settings_1.Settings.unavailableColor;
                        }
                    });
                }
            }
        })));
        this.layout();
    }
    get scrollSize() {
        return { width: this.app.screen.width, height: this.app.screen.height - (0, Global_1.px)(this.isLandscape ? 0 : this.top) };
    }
    makeMenu() {
        // Battle Button
        const mbutton = new MenuButton_1.MenuButton(this.app, i18next_1.default.t('mainmenu.create').toString(), settings_1.Settings.battleColor, this.size * 2, this.size * 1.2, this.size / 10, this.size > 140 ? 6 : 4, this.size / 3.625);
        this.battleButton = mbutton.asButton();
        this.battleButton.name = 'game' + interfaces_1.GameType.BattleFriends;
        const battleSprite = new BattleSprite_1.BattleSprite(this.app, true);
        battleSprite.name = 'battleSprite';
        mbutton.addChild(battleSprite);
        this.sigs.push(this.battleButton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            AssetManager_1.default.tickSound();
            yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateBattle, this.battleButton);
        })));
        this.sigs.push(this.battleButton.onHover.connect(() => __awaiter(this, void 0, void 0, function* () {
            battleSprite.play();
        })));
        // Guess Button
        const gbutton = new MenuButton_1.MenuButton(this.app, i18next_1.default.t('mainmenu.create2').toString(), settings_1.Settings.guessColor, this.size * 2, this.size * 1.2, this.size / 10, this.size > 140 ? 6 : 4, this.size / 3.625);
        this.guessButton = gbutton.asButton();
        this.guessButton.name = 'game' + interfaces_1.GameType.GuessFriends;
        const guessSprite = new GuessSprite_1.GuessSprite(this.app);
        guessSprite.name = 'guessSprite';
        gbutton.addChild(guessSprite);
        this.sigs.push(this.guessButton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            AssetManager_1.default.tickSound();
            yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateGuess, this.guessButton);
        })));
        this.sigs.push(this.guessButton.onHover.connect(() => {
            guessSprite.play();
        }));
        this.sigs.push(this.guessButton.onOut.connect(() => {
            guessSprite.stop();
        }));
        // Invite Field
        this.textfield = this.makeTextfield({
            placeholder: i18next_1.default.t('mainmenu.invite'),
            value: null,
            fontSize: this.size / 5.5,
            width: this.battleButton.width / (0, Global_1.px)(),
            maxLength: 6,
            height: this.battleButton.height / (0, Global_1.px)(),
            focusClear: true,
            wiggles: true
        });
        this.textfield.name = 'invite-field';
        this.textfield.moveWhenEditing = true;
        const updateTextColor = (color) => {
            this.textfield.children.filter((c) => (c instanceof pixi_js_1.Text)).forEach((t) => {
                t.style.fill = color;
            });
        };
        this.sigs.push(this.textfield.onEnter.connect((val) => {
            this.textfield.value = '';
            updateTextColor(settings_1.Settings.grayColor);
        }));
        this.sigs.push(this.textfield.onChange.connect((val) => {
            if (val && val.trim().length === 6) {
                // Really the type does not matter here since all that is important is the number
                this.gameManager.enterRoom(interfaces_1.GameType.BattleFriends, val.trim());
            }
            else {
                updateTextColor(settings_1.Settings.blackColor);
            }
        }));
        const container = new pixi_js_1.Container();
        container.addChild(this.textfield);
        return [this.battleButton, this.guessButton, container];
    }
    get size() {
        return this.overSmallSize ? 150 : 125;
    }
    get space() {
        return this.overSmallSize ? 22 : 14;
    }
    get bgoptions() {
        return Object.assign(Object.assign({}, super.bgoptions), { vertical: true });
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Find our button
            const mybutton = to.getChildByName('others', true);
            if (mybutton instanceof ui_1.ButtonContainer) {
                this.zIndex = SceneManager_1.Layer.Top;
                yield this.animateMask(mybutton, true);
            }
            return _super.exitScene.call(this, to);
        });
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = ((this.battleButton.getChildByName('battleSprite', true)))) === null || _a === void 0 ? void 0 : _a.play();
            (_b = ((this.guessButton.getChildByName('guessSprite', true)))) === null || _b === void 0 ? void 0 : _b.play();
            // Have a cool zoom animation
            if (target instanceof ui_1.ButtonContainer && from instanceof MenuScene_1.MenuScene) {
                yield this.animateMask(target);
            }
            return _super.enterScene.call(this, from);
        });
    }
    destroy(_options) {
        this.scrollBox.items.forEach((item) => {
            item.children.forEach((o) => o.destroy(_options));
        });
        this.textfield.destroy(_options);
        super.destroy(_options);
    }
    layout() {
        var _a, _b;
        super.layout();
        // Battle Button
        const battleButton = (this.battleButton.getChildAt(0));
        battleButton.w = this.size * 2;
        battleButton.h = this.size * 1.2;
        battleButton.padding = this.size / 10;
        battleButton.radius = this.size > 140 ? 6 : 4;
        battleButton.font = this.size / 3.625;
        battleButton.position.x = this.center.x - battleButton.width / 2;
        battleButton.layout();
        const bs = battleButton.getChildByName('battleSprite');
        if (bs) {
            bs.position.set((0, Global_1.px)(this.size), (0, Global_1.px)(battleButton.padding));
            bs.layout();
        }
        // Guess Button
        const guessButton = (this.guessButton.getChildAt(0));
        guessButton.w = this.size * 2;
        guessButton.h = this.size * 1.2;
        guessButton.padding = this.size / 10;
        guessButton.radius = this.size > 140 ? 6 : 4;
        guessButton.font = this.size / 3.625;
        guessButton.position.x = this.center.x - guessButton.width / 2;
        guessButton.layout();
        const gs = guessButton.getChildByName('guessSprite');
        if (gs) {
            gs.position.set((0, Global_1.px)(this.size), (0, Global_1.px)(guessButton.padding + 42));
            gs.layout();
        }
        // Textfield
        this.makeBox({
            width: this.size * 2,
            height: this.size / 3
        }, (this.textfield.bg));
        this.textfield.position.x = guessButton.x;
        this.textfield.layout(this.size / 5.5);
        this.scrollBox.position.set(0, this.isLandscapeBox ? 0 : (0, Global_1.px)(this.top));
        this.scrollBox.width = this.scrollSize.width;
        this.scrollBox.height = this.scrollSize.height;
        (_a = this.tshare) === null || _a === void 0 ? void 0 : _a.scale.set((0, Global_1.px)(this.overSmallSize ? 0.26 : 0.22));
        (_b = this.tshare) === null || _b === void 0 ? void 0 : _b.position.set((0, Global_1.right)(this.app.screen.width, 30), (0, Global_1.px)(30));
    }
}
exports.OtherModesScene = OtherModesScene;
